// src/layouts/AdminLayout.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Menu,
  X,
  Settings,
  LayoutDashboard,
  Users,
  BookOpen,
  ClipboardList,
  Star,
  Megaphone,
  Calendar,
  BarChart2,
  UserCog,
  ChevronDown,
  ChevronRight,
  Shield,
  Globe2,
  CalendarCheck,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation } from "../app/api/authApi";
import { clearCredentials } from "../app/features/authSlice";
import { useGetAdminStatsQuery } from "../app/api/adminApi";
import type { UserRole } from "../app/api/authApi";
import { ROLE_META } from "../pages/admin/AdminDashboard";

/* ─────────────────────────────────────────────────────────────────
   ROLE HELPERS
───────────────────────────────────────────────────────────────── */

// Content roles — can create tasks, library content, programme cycles
const CONTENT_ROLES: UserRole[] = [
  "admin",
  "co",
  "cyp",
  "ryp",
  "mentor",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "regional_rep",
];

// Announcement roles — can broadcast announcements
const ANNOUNCEMENT_ROLES: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "regional_rep",
];

// User management — admin and chair only
const USER_MGMT_ROLES: UserRole[] = [
  "admin",
  "committee_chair",
  "co",
  "cyp",
  "ryp",
];

// User management — admin and chair only
const REGION_MGMT_ROLES: UserRole[] = ["admin", "committee_chair", "co", "cyp"];

/* ─────────────────────────────────────────────────────────────────
   NAV TYPES
───────────────────────────────────────────────────────────────── */

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColour?: string;
  roles?: UserRole[]; // undefined = visible to all admin roles
  children?: Omit<NavItem, "children">[];
}

/* ─────────────────────────────────────────────────────────────────
   ADMIN LAYOUT
───────────────────────────────────────────────────────────────── */

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => window.innerWidth >= 768,
  );
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    programme: true, // expand the content group by default
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();

  const { user } = useAppSelector((s) => s.auth);
  const role = (user?.role ?? "admin") as UserRole;

  // Fetch summary stats once for badge counts
  const { data: statsRes } = useGetAdminStatsQuery();
  const stats = statsRes?.data;

  // Sidebar responsive behaviour
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 768) setSidebarOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch {
      /* ignore */
    } finally {
      dispatch(clearCredentials());
      navigate("/login");
    }
  }

  /* ── Helpers ── */
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName)
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    if (user?.firstName) return user.firstName[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "A";
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName)
      return `${user.firstName} ${user.lastName}`;
    if (user?.firstName) return user.firstName;
    return user?.email ?? "Admin";
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isGroupActive = (item: NavItem) =>
    item.children?.some((c) => isActive(c.path)) ?? false;

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── Nav definition ── */
  const NAV_ITEMS: NavItem[] = useMemo(
    () => [
      // ── Overview ──────────────────────────────────────────────────
      {
        label: "Dashboard",
        path: "/admin/dashboard",
        icon: <LayoutDashboard size={17} />,
      },
      {
        label: "Analytics",
        path: "/admin/analytics",
        icon: <BarChart2 size={17} />,
        roles: USER_MGMT_ROLES,
      },
      {
        label: "Regions & Provinces",
        path: "/admin/regions",
        icon: <Globe2 size={17} />,
        roles: REGION_MGMT_ROLES,
      },

      // ── Programme ─────────────────────────────────────────────────
      {
        label: "Programme",
        path: "/admin/programme",
        icon: <Calendar size={17} />,
        roles: CONTENT_ROLES,
        children: [
          {
            label: "Cycles & Quarters",
            path: "/admin/programme/cycles",
            icon: <Calendar size={15} />,
            roles: CONTENT_ROLES,
          },
          {
            label: "Tasks",
            path: "/admin/programme/tasks",
            icon: <ClipboardList size={15} />,
            badge: stats?.tasks?.draft ? String(stats.tasks.draft) : undefined,
            badgeColour: "bg-amber-100 text-amber-700",
            roles: CONTENT_ROLES,
          },
          {
            label: "Library",
            path: "/admin/programme/library",
            icon: <BookOpen size={15} />,
            badge: stats?.library?.draft
              ? String(stats.library.draft)
              : undefined,
            badgeColour: "bg-blue-100 text-blue-700",
            roles: CONTENT_ROLES,
          },
        ],
      },

      // ── People ────────────────────────────────────────────────────
      {
        label: "Members",
        path: "/admin/members",
        icon: <Users size={17} />,
        children: [
          {
            label: "All Members",
            path: "/admin/members/all",
            icon: <Users size={15} />,
          },
          {
            label: "Inner Circle",
            path: "/admin/members/inner-circle",
            icon: <UserCog size={15} />,
          },
          {
            label: "Legacy Fellows",
            path: "/admin/members/fellows",
            icon: <Star size={15} />,
            badge: stats?.fellows?.selected
              ? String(stats.fellows.selected)
              : undefined,
            badgeColour: "bg-amber-100 text-amber-700",
          },
          {
            label: "Regional Reps",
            path: "/admin/members/reps",
            icon: <UserCog size={15} />,
            roles: USER_MGMT_ROLES,
          },
        ],
      },

      // ── Communications ────────────────────────────────────────────
      {
        label: "Announcements",
        path: "/admin/announcements",
        icon: <Megaphone size={17} />,
        roles: ANNOUNCEMENT_ROLES,
        badge: stats?.announcements?.published
          ? String(stats.announcements.published)
          : undefined,
        badgeColour: "bg-green-100 text-green-700",
      },
      {
        label: "Events",
        path: "/admin/events",
        icon: <CalendarCheck size={17} />,
        roles: [
          "admin",
          "co",
          "cyp",
          "committee_member",
          "committee_chair",
        ] as UserRole[],
      },

      // ── Admin Team ────────────────────────────────────────────────
      {
        label: "Admin Team",
        path: "/admin/team",
        icon: <Shield size={17} />,
        roles: ["admin", "co", "cyp"] as UserRole[],
      },
    ],
    [stats],
  );

  // Filter nav items by role
  const visibleNav = useMemo(
    () =>
      NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role)).map(
        (item) => ({
          ...item,
          children: item.children?.filter(
            (c) => !c.roles || c.roles.includes(role),
          ),
        }),
      ),
    [NAV_ITEMS, role],
  );

  // Current page title — derived from the active nav item
  const pageTitle = useMemo(() => {
    for (const item of visibleNav) {
      if (item.children) {
        const match = item.children.find((c) => isActive(c.path));
        if (match) return match.label;
      }
      if (isActive(item.path)) return item.label;
    }
    return "Admin";
  }, [isActive, visibleNav]);

  const roleMeta = ROLE_META[role] ?? ROLE_META["admin"];

  /* ─────────────────────────────────────────────────────────────── */

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ══ SIDEBAR ═══════════════════════════════════════════════ */}
      <aside
        className={`
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        ${sidebarOpen ? "md:w-64" : "md:w-0"}
        fixed md:static inset-y-0 left-0 z-30 w-64 bg-white flex-shrink-0
        transition-all duration-300 border-r overflow-hidden flex flex-col
      `}
      >
        {/* Brand */}
        <div className="shrink-0 p-4 border-b">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center font-bold text-lg text-white shrink-0">
              G
            </div>
            <div>
              <p className="font-bold text-sm text-gray-900 leading-tight">
                GIMLP
              </p>
              <p className="text-[11px] text-gray-400">Admin Panel</p>
            </div>
          </Link>
        </div>

        {/* NAV */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
          {visibleNav.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const groupKey = item.path;
            const expanded = openGroups[groupKey] ?? isGroupActive(item);
            const active = !hasChildren && isActive(item.path);

            if (hasChildren) {
              return (
                <div key={item.path}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left
                      ${
                        isGroupActive(item)
                          ? "bg-purple-50 text-purple-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    <span
                      className={`shrink-0 ${isGroupActive(item) ? "text-purple-600" : "text-gray-400"}`}
                    >
                      {item.icon}
                    </span>
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full mr-1 ${item.badgeColour ?? "bg-gray-100 text-gray-500"}`}
                      >
                        {item.badge}
                      </span>
                    )}
                    {expanded ? (
                      <ChevronDown
                        size={13}
                        className="shrink-0 text-gray-400"
                      />
                    ) : (
                      <ChevronRight
                        size={13}
                        className="shrink-0 text-gray-400"
                      />
                    )}
                  </button>

                  {/* Children */}
                  {expanded && (
                    <div className="ml-3 mt-0.5 pl-3 border-l border-gray-100 space-y-0.5 mb-1">
                      {item.children!.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm transition-colors
                            ${
                              isActive(child.path)
                                ? "bg-purple-50 text-purple-700 font-semibold"
                                : "text-gray-500 hover:bg-gray-100 hover:text-gray-800 font-medium"
                            }`}
                        >
                          <span
                            className={`shrink-0 ${isActive(child.path) ? "text-purple-500" : "text-gray-400"}`}
                          >
                            {child.icon}
                          </span>
                          <span className="flex-1">{child.label}</span>
                          {child.badge && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${child.badgeColour ?? "bg-gray-100 text-gray-500"}`}
                            >
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Flat item
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${
                    active
                      ? "bg-purple-50 text-purple-700"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                <span
                  className={`shrink-0 ${active ? "text-purple-600" : "text-gray-400"}`}
                >
                  {item.icon}
                </span>
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${item.badgeColour ?? "bg-gray-100 text-gray-500"}`}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* FOOTER */}
        <div className="shrink-0 p-3 border-t">
          {/* User info */}
          <div className="flex items-center gap-3 px-1 mb-3">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={getDisplayName()}
                className="w-9 h-9 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                {getUserInitials()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                {getDisplayName()}
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>

          {/* Role badge */}
          <div
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full mb-3 ${roleMeta.colour}`}
          >
            <Shield size={10} />
            {roleMeta.label}
          </div>

          {/* Action buttons */}
          <div className="space-y-0.5">
            <button
              onClick={() => navigate("/admin/settings")}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-gray-600"
            >
              <Settings size={15} />
              <span>Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm text-gray-600"
            >
              <LogOut size={15} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ══ MAIN ══════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TOP BAR */}
        <header className="shrink-0 bg-white border-b px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            <div>
              <h1 className="text-base font-bold text-gray-900 leading-tight">
                {pageTitle}
              </h1>
              <p className="text-[11px] text-gray-400">
                Welcome back, {user?.firstName ?? "Admin"}
              </p>
            </div>
          </div>

          {/* Top-bar stats chips — visible on md+ */}
          {stats && (
            <div className="hidden md:flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                <Users size={11} />
                {stats.users.activeMentees} active members
              </span>
              {stats.programme.activeCycle && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-purple-100 text-purple-700">
                  <Calendar size={11} />
                  {stats.programme.activeCycle.quarter} active
                </span>
              )}
            </div>
          )}

          {/* Role badge */}
          <span
            className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${roleMeta.colour}`}
          >
            <Shield size={10} />
            {roleMeta.label}
          </span>
        </header>

        {/* PAGE CONTENT */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
